import { useCallback, useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { Check, ListChecks, Pencil, Plus, Trash2, X } from 'lucide-react'
import { toast } from 'react-toastify'
import ResponsiveShell from '../components/layout/ResponsiveShell'
import {
  createCustomStatus,
  deleteCustomStatus,
  getCachedCustomStatuses,
  invalidateCustomStatusesCache,
  patchCustomStatus,
} from '../api/customStatuses'
import useManagerStore from '../store/managerStore'
import { DEFAULT_TAG_COLOR } from '../utils/contactTags'

const PRESET_COLORS = [
  '#1535c8', '#0f766e', '#16a34a', '#ca8a04',
  '#dc2626', '#c026d3', '#7c3aed', '#475569',
]

const ENTITY_TYPES = ['booking', 'order']

const TERMINAL_TYPES = ['none', 'completed_like', 'cancelled_like']

function slugify(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    || 'status'
}

function normalizeHexColor(value) {
  const raw = String(value || '').trim()
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw.toLowerCase()
  if (/^[0-9a-f]{6}$/i.test(raw)) return `#${raw.toLowerCase()}`
  return ''
}

function emptyDraft(entityType) {
  return {
    label: '',
    color: DEFAULT_TAG_COLOR,
    terminal_type: entityType === 'booking' ? 'none' : null,
    is_terminal: false,
    counts_as_revenue: false,
  }
}

function StatusChip({ label, color }) {
  const c = normalizeHexColor(color) || DEFAULT_TAG_COLOR
  return (
    <span
      className="inline-flex max-w-full items-center rounded-full px-2.5 py-1 text-xs font-semibold"
      style={{ backgroundColor: `${c}1f`, color: c }}
    >
      <span className="truncate">{label}</span>
    </span>
  )
}

function BehaviorBadge({ entityType, status, t }) {
  if (entityType === 'booking') {
    const key = status.terminal_type || 'none'
    const labelKey = {
      none: 'customStatusPage.terminalTypeNone',
      completed_like: 'customStatusPage.terminalTypeCompletedLike',
      cancelled_like: 'customStatusPage.terminalTypeCancelledLike',
    }[key]
    return <span className="text-xs text-slate-500">{t(labelKey)}</span>
  }
  return (
    <div className="flex flex-wrap gap-1">
      {status.is_terminal && (
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
          {t('customStatusPage.isTerminalLabel')}
        </span>
      )}
      {status.counts_as_revenue && (
        <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] text-emerald-700">
          {t('customStatusPage.countsAsRevenueLabel')}
        </span>
      )}
    </div>
  )
}

export default function CustomStatusPage() {
  const { t } = useTranslation()
  const tenantId = useManagerStore((s) => s.tenantId)
  const [entityType, setEntityType] = useState('booking')
  const [statuses, setStatuses] = useState([])
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState(emptyDraft('booking'))
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  const loadStatuses = useCallback(async (type) => {
    if (!tenantId) {
      setStatuses([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const items = await getCachedCustomStatuses(tenantId, type)
      setStatuses(Array.isArray(items) ? items : [])
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'load_custom_statuses' }, extra: { tenantId, entityType: type } })
      toast.error(t('customStatusPage.loadFailed'))
      setStatuses([])
    } finally {
      setLoading(false)
    }
  }, [tenantId, t])

  useEffect(() => {
    loadStatuses(entityType)
  }, [loadStatuses, entityType])

  function switchEntityType(type) {
    if (type === entityType) return
    setEntityType(type)
    resetDraft(type)
  }

  function resetDraft(type = entityType) {
    setDraft(emptyDraft(type))
    setEditingId(null)
  }

  function startEdit(status) {
    if (status.is_default) return
    setEditingId(status.id)
    setDraft({
      label: status.label,
      color: normalizeHexColor(status.color) || DEFAULT_TAG_COLOR,
      terminal_type: status.terminal_type ?? 'none',
      is_terminal: !!status.is_terminal,
      counts_as_revenue: !!status.counts_as_revenue,
    })
  }

  async function handleSubmit(event) {
    event.preventDefault()
    if (!tenantId || saving) return
    const label = draft.label.trim()
    if (!label) return
    const color = normalizeHexColor(draft.color) || DEFAULT_TAG_COLOR

    setSaving(true)
    try {
      if (editingId) {
        const body = entityType === 'booking'
          ? { label, color, terminal_type: draft.terminal_type }
          : { label, color, is_terminal: draft.is_terminal, counts_as_revenue: draft.counts_as_revenue }
        await patchCustomStatus(tenantId, editingId, body)
        toast.success(t('customStatusPage.updateSuccess'))
      } else {
        const body = entityType === 'booking'
          ? { entity_type: 'booking', slug: slugify(label), label, color, terminal_type: draft.terminal_type }
          : { entity_type: 'order', slug: slugify(label), label, color, is_terminal: draft.is_terminal, counts_as_revenue: draft.counts_as_revenue }
        await createCustomStatus(tenantId, body)
        toast.success(t('customStatusPage.createSuccess'))
      }
      invalidateCustomStatusesCache(tenantId, entityType)
      resetDraft()
      await loadStatuses(entityType)
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: editingId ? 'update_custom_status' : 'create_custom_status' },
        extra: { tenantId, entityType, statusId: editingId },
      })
      toast.error(t(editingId ? 'customStatusPage.updateFailed' : 'customStatusPage.createFailed'))
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(status) {
    if (!tenantId || status.is_default || deletingId) return
    const confirmed = window.confirm(t('customStatusPage.deleteConfirm', { name: status.label }))
    if (!confirmed) return

    setDeletingId(status.id)
    try {
      await deleteCustomStatus(tenantId, status.id)
      invalidateCustomStatusesCache(tenantId, entityType)
      setStatuses(prev => prev.filter(item => String(item.id) !== String(status.id)))
      if (String(editingId) === String(status.id)) resetDraft()
      toast.success(t('customStatusPage.deleteSuccess'))
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'delete_custom_status' }, extra: { tenantId, statusId: status.id } })
      toast.error(t('customStatusPage.deleteFailed'))
    } finally {
      setDeletingId(null)
    }
  }

  const colorValue = normalizeHexColor(draft.color) || DEFAULT_TAG_COLOR

  return (
    <ResponsiveShell title={t('customStatusPage.title')}>
      <div className="mx-auto max-w-6xl px-0 pb-10 lg:px-8">
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xl font-bold text-slate-950">
              <ListChecks size={22} className="text-[#1535c8]" />
              <h1 className="truncate">{t('customStatusPage.title')}</h1>
            </div>
            <p className="mt-1 text-sm text-slate-500">{t('customStatusPage.subtitle')}</p>
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
            {ENTITY_TYPES.map(type => (
              <button
                key={type}
                type="button"
                onClick={() => switchEntityType(type)}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${
                  entityType === type ? 'bg-[#1535c8] text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {t(type === 'booking' ? 'customStatusPage.entityBooking' : 'customStatusPage.entityOrder')}
              </button>
            ))}
          </div>
        </div>

        {!tenantId ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            {t('customStatusPage.needTenant')}
          </div>
        ) : (
          <div className="grid gap-5 lg:grid-cols-[360px_minmax(0,1fr)]">
            <section className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-base font-bold text-slate-900">
                  {editingId ? t('customStatusPage.editTitle') : t('customStatusPage.createTitle')}
                </h2>
                {editingId && (
                  <button
                    type="button"
                    onClick={() => resetDraft()}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
                    aria-label={t('common.cancel')}
                    title={t('common.cancel')}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">
                    {t('customStatusPage.labelLabel')}
                  </label>
                  <input
                    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-900 outline-none focus:border-[#1535c8] focus:ring-2 focus:ring-[#1535c8]/10"
                    value={draft.label}
                    onChange={event => setDraft(prev => ({ ...prev, label: event.target.value }))}
                    placeholder={t('customStatusPage.labelPlaceholder')}
                    maxLength={120}
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">
                    {t('customStatusPage.colorLabel')}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={colorValue}
                      onChange={event => setDraft(prev => ({ ...prev, color: event.target.value }))}
                      className="h-10 w-12 cursor-pointer rounded-lg border border-slate-200 bg-white p-1"
                      aria-label={t('customStatusPage.colorLabel')}
                    />
                    <input
                      className="h-10 min-w-0 flex-1 rounded-lg border border-slate-200 px-3 font-mono text-sm uppercase text-slate-900 outline-none focus:border-[#1535c8] focus:ring-2 focus:ring-[#1535c8]/10"
                      value={draft.color}
                      onChange={event => setDraft(prev => ({ ...prev, color: event.target.value }))}
                      maxLength={7}
                    />
                  </div>
                  <div className="mt-3 grid grid-cols-8 gap-2">
                    {PRESET_COLORS.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setDraft(prev => ({ ...prev, color }))}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-black/10"
                        style={{ backgroundColor: color }}
                        title={color}
                      >
                        {colorValue === color && <Check size={15} className="text-white" strokeWidth={3} />}
                      </button>
                    ))}
                  </div>
                </div>

                {entityType === 'booking' ? (
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">
                      {t('customStatusPage.terminalTypeLabel')}
                    </label>
                    <select
                      className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-900 outline-none focus:border-[#1535c8] focus:ring-2 focus:ring-[#1535c8]/10"
                      value={draft.terminal_type ?? 'none'}
                      onChange={event => setDraft(prev => ({ ...prev, terminal_type: event.target.value }))}
                    >
                      {TERMINAL_TYPES.map(v => (
                        <option key={v} value={v}>
                          {t(`customStatusPage.terminalType${v === 'none' ? 'None' : v === 'completed_like' ? 'CompletedLike' : 'CancelledLike'}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={draft.is_terminal}
                        onChange={event => setDraft(prev => ({ ...prev, is_terminal: event.target.checked }))}
                      />
                      {t('customStatusPage.isTerminalLabel')}
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={draft.counts_as_revenue}
                        onChange={event => setDraft(prev => ({ ...prev, counts_as_revenue: event.target.checked }))}
                      />
                      {t('customStatusPage.countsAsRevenueLabel')}
                    </label>
                  </div>
                )}

                <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <StatusChip label={draft.label.trim() || '...'} color={colorValue} />
                </div>

                <button
                  type="submit"
                  disabled={saving || !draft.label.trim()}
                  className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-[#1535c8] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {editingId ? <Check size={16} /> : <Plus size={16} />}
                  {saving
                    ? t('common.saving')
                    : editingId
                      ? t('customStatusPage.saveEdit')
                      : t('customStatusPage.createButton')}
                </button>
              </form>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white">
              <div className="hidden overflow-hidden rounded-lg lg:block">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      <th className="border-b border-slate-100 px-4 py-3 text-xs font-semibold uppercase text-slate-500">
                        {t('customStatusPage.columns.status')}
                      </th>
                      <th className="border-b border-slate-100 px-4 py-3 text-xs font-semibold uppercase text-slate-500">
                        {t('customStatusPage.columns.behavior')}
                      </th>
                      <th className="border-b border-slate-100 px-4 py-3 text-right text-xs font-semibold uppercase text-slate-500">
                        {t('customStatusPage.columns.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-10 text-center text-sm text-slate-400">
                          {t('common.loading')}
                        </td>
                      </tr>
                    ) : statuses.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-10 text-center text-sm text-slate-400">
                          {t('customStatusPage.empty')}
                        </td>
                      </tr>
                    ) : (
                      statuses.map(status => (
                        <tr key={status.id} className="border-b border-slate-100 last:border-b-0">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <StatusChip label={status.label} color={status.color} />
                              {status.is_default && (
                                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">
                                  {t('customStatusPage.defaultBadge')}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <BehaviorBadge entityType={entityType} status={status} t={t} />
                          </td>
                          <td className="px-4 py-3">
                            {status.is_default ? (
                              <div className="text-right text-xs text-slate-400" title={t('customStatusPage.defaultReadOnlyHint')}>
                                {t('customStatusPage.defaultReadOnlyHint')}
                              </div>
                            ) : (
                              <div className="flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => startEdit(status)}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                                  aria-label={t('common.edit')}
                                  title={t('common.edit')}
                                >
                                  <Pencil size={15} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(status)}
                                  disabled={deletingId === status.id}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-100 text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                                  aria-label={t('common.delete')}
                                  title={t('common.delete')}
                                >
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col gap-2 p-3 lg:hidden">
                {loading ? (
                  <div className="py-8 text-center text-sm text-slate-400">{t('common.loading')}</div>
                ) : statuses.length === 0 ? (
                  <div className="py-8 text-center text-sm text-slate-400">{t('customStatusPage.empty')}</div>
                ) : (
                  statuses.map(status => (
                    <div key={status.id} className="rounded-lg border border-slate-100 p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2">
                          <StatusChip label={status.label} color={status.color} />
                          {status.is_default && (
                            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">
                              {t('customStatusPage.defaultBadge')}
                            </span>
                          )}
                        </div>
                        {!status.is_default && (
                          <div className="flex shrink-0 gap-2">
                            <button
                              type="button"
                              onClick={() => startEdit(status)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600"
                              aria-label={t('common.edit')}
                              title={t('common.edit')}
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(status)}
                              disabled={deletingId === status.id}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-100 text-red-500 disabled:opacity-60"
                              aria-label={t('common.delete')}
                              title={t('common.delete')}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="mt-2">
                        <BehaviorBadge entityType={entityType} status={status} t={t} />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </ResponsiveShell>
  )
}
